/**
 * Cache with TTL support for frontend.
 * Uses memory for speed + sessionStorage for persistence across page reloads.
 */
class ApiCache {
  constructor() {
    this.storagePrefix = 'fk_cache:';
    this.cache = new Map();
  }

  canUseStorage() {
    return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
  }

  getStorageKey(key) {
    return `${this.storagePrefix}${key}`;
  }

  readFromStorage(key) {
    if (!this.canUseStorage()) return null;
    try {
      const raw = window.sessionStorage.getItem(this.getStorageKey(key));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch {
      return null;
    }
  }

  writeToStorage(key, payload) {
    if (!this.canUseStorage()) return;
    try {
      window.sessionStorage.setItem(this.getStorageKey(key), JSON.stringify(payload));
    } catch {
      // Ignore quota/storage exceptions; memory cache still works.
    }
  }

  deleteFromStorage(key) {
    if (!this.canUseStorage()) return;
    try {
      window.sessionStorage.removeItem(this.getStorageKey(key));
    } catch {
      // ignore
    }
  }

  /**
   * Get a cached value
   * @param {string} key - Cache key
   * @returns {any} Cached value or null if expired/not found
   */
  get(key) {
    const memoryItem = this.cache.get(key);
    if (memoryItem) {
      if (Date.now() > memoryItem.expiry) {
        this.cache.delete(key);
        this.deleteFromStorage(key);
        return null;
      }
      return memoryItem.value;
    }

    const storageItem = this.readFromStorage(key);
    if (!storageItem || typeof storageItem.expiry !== 'number') return null;

    if (Date.now() > storageItem.expiry) {
      this.deleteFromStorage(key);
      return null;
    }

    // Rehydrate memory for faster subsequent access.
    this.cache.set(key, storageItem);
    return storageItem.value;
  }

  /**
   * Set a cached value
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttlSeconds - Time to live in seconds (default 5 minutes)
   */
  set(key, value, ttlSeconds = 300) {
    const payload = {
      value,
      expiry: Date.now() + (ttlSeconds * 1000),
    };
    this.cache.set(key, payload);
    this.writeToStorage(key, payload);
  }

  /**
   * Delete a cached value
   * @param {string} key - Cache key
   */
  delete(key) {
    this.cache.delete(key);
    this.deleteFromStorage(key);
  }

  /**
   * Delete all cached values matching a prefix
   * @param {string} prefix - Key prefix to match
   */
  deleteByPrefix(prefix) {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }

    if (this.canUseStorage()) {
      const keysToDelete = [];
      for (let i = 0; i < window.sessionStorage.length; i += 1) {
        const fullKey = window.sessionStorage.key(i);
        if (!fullKey || !fullKey.startsWith(this.storagePrefix)) continue;
        const cacheKey = fullKey.slice(this.storagePrefix.length);
        if (cacheKey.startsWith(prefix)) {
          keysToDelete.push(fullKey);
        }
      }
      keysToDelete.forEach((fullKey) => window.sessionStorage.removeItem(fullKey));
    }
  }

  /**
   * Clear all cached values
   */
  clear() {
    this.cache.clear();
    if (this.canUseStorage()) {
      const keysToDelete = [];
      for (let i = 0; i < window.sessionStorage.length; i += 1) {
        const fullKey = window.sessionStorage.key(i);
        if (fullKey && fullKey.startsWith(this.storagePrefix)) {
          keysToDelete.push(fullKey);
        }
      }
      keysToDelete.forEach((fullKey) => window.sessionStorage.removeItem(fullKey));
    }
  }
}

export const apiCache = new ApiCache();
export default apiCache;
